import { getDemoData } from '../data/dashboardDemoData';
import { buildAgencyHeroSlide } from '../data/exampleCompetitorMapData';

const BASE_DEMO_DATA = getDemoData();

/**
 * Shared demo data props for BusinessReviewModal on public landing pages,
 * agency hero embeds, and dashboard showcases. Centralizing here ensures
 * every demo surface stays in sync when the modal or demo dataset changes.
 */
export const DEMO_BUSINESS_REVIEW_DATA = {
  yearData: BASE_DEMO_DATA.yearData,
  sortedByTotal: BASE_DEMO_DATA.sortedByTotal,
  sortedBySentiment: BASE_DEMO_DATA.sortedBySentiment,
  sortedByGrowth: BASE_DEMO_DATA.sortedByGrowth,
  sortedByTraffic: BASE_DEMO_DATA.sortedByTraffic,
  sortedBySeo: BASE_DEMO_DATA.sortedBySeo,
  demoMode: true,
  demoTrafficHistory: BASE_DEMO_DATA.modalTrafficHistory,
  demoPricingData: BASE_DEMO_DATA.buildDemoPricing,
};

export const DEMO_BUSINESS_REVIEW_PRESENTATION = {
  // Map pins on the landing pages open the *full* business review module —
  // identical sizing/spacing to the in-app modal (90vw/90vh). It used to be a
  // condensed "compact" overlay (68vw/78vh), but on the fixed-width mobile
  // viewport that rendered far too cramped, so we now match the logged-in view.
  map: {},
  hero: { inline: true, hideCompetitorComparison: true },
  // Iframe embed on the agencies landing page — flush edge-to-edge inside
  // the parent rounded container (no inner card chrome or padding).
  embed: { inline: true, hideCompetitorComparison: true, flush: true },
  dashboard: {},
};

const AGENCY_HERO_VARIANTS = {
  'agency-hero-weak': 'weak',
  'agency-hero-strong': 'strong',
  'where-they-are': 'weak',
  'where-youll-take-them': 'strong',
};

function buildAgencyHeroDemoProps(dataVariant) {
  const slideVariant = AGENCY_HERO_VARIANTS[dataVariant] || 'weak';
  const slide = buildAgencyHeroSlide(slideVariant);

  return {
    business: slide.business,
    yearData: slide.yearData,
    sortedByTotal: slide.sortedByTotal,
    sortedBySentiment: slide.sortedBySentiment,
    sortedByGrowth: slide.sortedByGrowth,
    sortedByTraffic: slide.sortedByTraffic,
    sortedBySeo: slide.sortedBySeo,
    demoMode: true,
    demoTrafficHistory: slide.trafficHistory,
    demoPricingData: slide.pricingData,
  };
}

/**
 * Returns the full set of demo props for BusinessReviewModal.
 *
 * @param {'default'|'agency-hero-weak'|'agency-hero-strong'|'where-they-are'|'where-youll-take-them'} dataVariant
 */
export function getDemoBusinessReviewProps(dataVariant = 'default') {
  if (dataVariant !== 'default') {
    return buildAgencyHeroDemoProps(dataVariant);
  }

  return { ...DEMO_BUSINESS_REVIEW_DATA };
}

/**
 * Spread into BusinessReviewModal inside authenticated dashboards when demoMode is on.
 * Keeps landing showcase dashboards aligned with map/hero demo surfaces.
 */
export function getDashboardDemoBusinessReviewProps(demoMode, demoData) {
  if (!demoMode) {
    return {};
  }

  const source = demoData || BASE_DEMO_DATA;

  return {
    demoMode: true,
    demoTrafficHistory: source.modalTrafficHistory,
    demoPricingData: source.buildDemoPricing,
    sortedBySeo: source.sortedBySeo,
  };
}

export function getDemoBusinessReviewPresentation(presentation = 'dashboard') {
  return DEMO_BUSINESS_REVIEW_PRESENTATION[presentation] || DEMO_BUSINESS_REVIEW_PRESENTATION.dashboard;
}
